import { useLayoutEffect, useRef, useState } from "react";

// HOW THIS PART IS PUT TOGETHER — the student-facing projection of the backend's within-unit organization reading
// (`functional_map.organization`: rows of parts, links, parts set apart, the working part). NON-SEMANTIC: draws exactly the
// links the backend supplied — no id, no confidence, no legend; a tentative link is dashed, a loop curves back, contrast sits
// side by side with "vs", parts nothing connects sit apart. Persistent: a writing tool the student keeps.
const flat = (parts) => parts.flatMap((p) => [p, ...flat(p.parts || [])]);

// nested parts (genuine containment) are listed inside the parent's box — flattened to one indented list, never a second box
const Part = ({ p, register }) => {
  const inner = flat(p.parts || []);
  return (
    <div ref={(el) => register(p.key, el)} data-testid={`part-${p.key}`} data-working={p.working ? "1" : undefined}
      className={`rounded-lg border px-2.5 py-1.5 text-[12px] leading-snug ${p.working ? "border-amber-500 bg-amber-50 text-stone-900 ring-1 ring-amber-300" : "border-stone-300 bg-white text-stone-800"}`}
      style={{ minWidth: 120, maxWidth: 190 }}>
      {p.working && <span data-testid="part-working-mark" className="mr-1 font-mono text-[9px] uppercase tracking-[0.14em] text-amber-700">working here</span>}
      <span>{p.label}</span>
      {inner.length > 0 && (
        <div className="mt-1.5 space-y-1 border-l-2 border-stone-200 pl-2">
          {inner.map((c) => (
            <div key={c.key} ref={(el) => register(c.key, el)} data-testid={`part-${c.key}`} data-working={c.working ? "1" : undefined} className={`text-[11.5px] ${c.working ? "text-amber-800 font-medium" : "text-stone-700"}`}>
              {c.working && <span data-testid="part-working-mark" className="mr-1 font-mono text-[9px] uppercase tracking-[0.14em] text-amber-700">working here</span>}{c.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function PartOrganization({ org }) {
  const wrap = useRef(null);
  const boxes = useRef({});
  const [geo, setGeo] = useState({ w: 0, h: 0, paths: [] });
  const register = (key, el) => { if (el) boxes.current[key] = el; };
  const sig = JSON.stringify(org && org.links);
  useLayoutEffect(() => {
    if (!wrap.current || !org) return;
    const compute = () => {
      const W = wrap.current.getBoundingClientRect();
      const r = (k) => { const b = boxes.current[k]; if (!b) return null; const q = b.getBoundingClientRect(); return { x: q.left - W.left, y: q.top - W.top, w: q.width, h: q.height }; };
      const seen = {};
      const paths = org.links.map((l) => {
        const a = r(l.from), b = r(l.to);
        if (!a || !b) return null;
        const acx = a.x + a.w / 2, bcx = b.x + b.w / 2;
        const sameRow = Math.abs(a.y - b.y) < 8;
        let d, lx, ly;
        if (!l.directed && sameRow) {                                  // contrast / tension: beside each other, a short bar with the word
          const [L, R] = a.x < b.x ? [a, b] : [b, a];
          const y = L.y + Math.min(L.h, R.h) / 2;
          const gap = R.x - (L.x + L.w);
          d = gap < 90 ? `M${L.x + L.w},${y} L${R.x},${y}` : `M${L.x + L.w / 2},${L.y} C${L.x + L.w / 2},${L.y - 26} ${R.x + R.w / 2},${R.y - 26} ${R.x + R.w / 2},${R.y}`;
          lx = gap < 90 ? L.x + L.w + gap / 2 : (L.x + L.w / 2 + R.x + R.w / 2) / 2; ly = gap < 90 ? y - 7 : L.y - 22;
        } else if (l.loop || b.y < a.y) {                               // a back-edge: out to the right of both parts and back up — a visible loop
          const right = Math.max(a.x + a.w, b.x + b.w) + 26;
          d = `M${a.x + a.w},${a.y + a.h / 2} C${right},${a.y + a.h / 2} ${right},${b.y + b.h / 2} ${b.x + b.w},${b.y + b.h / 2}`;
          lx = right + 4; ly = (a.y + b.y + b.h) / 2;
        } else {                                                        // forward: bottom of the source to the top of the target
          d = `M${acx},${a.y + a.h} C${acx},${a.y + a.h + 22} ${bcx},${b.y - 22} ${bcx},${b.y}`;
          const n = (seen[l.to] = (seen[l.to] || 0) + 1);                // several links into one part: stagger their words so none collide
          lx = (acx + bcx) / 2 + (acx < bcx ? -10 : 10); ly = b.y - 12 - (n - 1) * 12;
        }
        return { ...l, d, lx, ly };
      }).filter(Boolean);
      setGeo({ w: W.width, h: wrap.current.scrollHeight, paths });
    };
    compute();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(compute); ro.observe(wrap.current);
    return () => ro.disconnect();
  }, [sig, org]);
  if (!org || !org.rows) return null;
  const working = flat(org.rows.flat()).concat(flat(org.apart || [])).find((p) => p.working);
  return (
    <div data-testid="part-organization" className="space-y-2">
      {org.whole ? <p data-testid="part-organization-whole" className="text-[12.5px] text-stone-800"><span className="font-mono text-[9.5px] uppercase tracking-[0.16em] text-stone-400 mr-2">this part says</span>{org.whole}</p>
        : <p data-testid="part-organization-no-whole" className="text-[11.5px] italic text-stone-500">No single main point holds these together yet.</p>}
      {org.standing && <p data-testid="part-organization-standing" className="text-[11px] text-stone-500">{org.standing}</p>}
      <div ref={wrap} className="relative">
        <svg data-testid="part-organization-links" className="pointer-events-none absolute left-0 top-0" width={geo.w || 1} height={geo.h || 1} style={{ overflow: "visible" }}>
          <defs><marker id="po-arrow" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="6" markerHeight="6" orient="auto"><path d="M0,0 L8,4 L0,8 z" fill="#78716c" /></marker></defs>
          {geo.paths.map((p, i) => (
            <g key={i} data-testid={`link-${p.from}-${p.to}`} data-kind={p.kind} data-tentative={p.tentative ? "1" : undefined} data-loop={p.loop ? "1" : undefined}>
              <path d={p.d} fill="none" stroke={p.tentative ? "#a8a29e" : "#78716c"} strokeWidth={1.2} strokeDasharray={p.tentative ? "3 4" : undefined}
                markerEnd={p.directed ? "url(#po-arrow)" : undefined} />
              {p.word && <text x={p.lx} y={p.ly} fontSize="10" textAnchor="middle" fill="#57534e" className="font-mono" style={{ paintOrder: "stroke", stroke: "white", strokeWidth: 3 }}>{p.word}{p.tentative ? "?" : ""}</text>}
            </g>
          ))}
        </svg>
        <div className="relative space-y-9">
          {org.rows.map((row, i) => (
            <div key={i} data-testid={`part-row-${i}`} className="flex flex-wrap items-start justify-center gap-x-10 gap-y-3">
              {row.map((p) => <Part key={p.key} p={p} register={register} />)}
            </div>
          ))}
        </div>
        {org.apart && org.apart.length > 0 && (
          <div data-testid="part-organization-apart" className="mt-8 border-t border-dashed border-stone-300 pt-3">
            <p className="mb-2 text-[10.5px] italic text-stone-500">Not yet connected to the rest</p>
            <div className="flex flex-wrap gap-x-8 gap-y-3">{org.apart.map((p) => <Part key={p.key} p={p} register={register} />)}</div>
          </div>
        )}
      </div>
      {working && <p data-testid="part-organization-working" className="text-[11.5px] text-stone-600"><span className="font-mono text-[9.5px] uppercase tracking-[0.14em] text-amber-700 mr-2">working here</span>{working.label}</p>}
    </div>
  );
}
